import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  FileText,
  Building2,
  Trash2,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Users,
  Hash,
  ChevronDown,
  Plus,
  X,
  NotebookPen,
  Edit2,
  Edit,
  CheckCircle2,
  Activity,
} from "lucide-react";
import Facebook from "@mui/icons-material/Facebook";
import Instagram from "@mui/icons-material/Instagram";
import LinkedIn from "@mui/icons-material/LinkedIn";
import YouTube from "@mui/icons-material/YouTube";
import { TikTokIcon } from "../../components/icons/TikTokIcon";
import Article from "@mui/icons-material/Article";
import Email from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import Language from "@mui/icons-material/Language";
import ChecklistRtl from "@mui/icons-material/ChecklistRtl";
import BarChart from "@mui/icons-material/BarChart";
import { GoogleIcon } from "../../components/icons/GoogleIcon";
import { GoogleDriveIcon } from "../../components/icons/GoogleDriveIcon";
import { Card, CardContent } from "../../components/Card";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { CustomTextArea } from "../../components/CustomTextArea";
import { CustomSelector } from "../../components/CustomSelector";
import { Label } from "../../components/Label";
import { Separator } from "../../components/Separator";
import { Tabs, TabsContent } from "../../components/Tabs";
import { Checkbox } from "../../components/Checkbox";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "../../components/Collapsible";
import { UnifiedModal } from "../../components/UnifiedModal";
import { useAuth } from "../../components/contexts/AuthContext";
import { useTheme } from "../../theme/useTheme";
import { InfoTooltip } from "../../components/InfoTooltip";
import { getData } from "../../components/hooks/getData";
import { patchWithRefresh, deleteClientDigitalAsset } from "../../api-client";
import { brand, platformColors } from "../../theme/theme";
import { isPublishablePlatform } from "../ContentGenerator/workflow/platformMeta";
import { readPortalReturn, useSocialConnections } from "./social/useSocialConnections";
import { ClientConnections } from "./social/ClientConnections";
import UnsavedChangesBar from "../../components/UnsavedChangesBar";

export const SingleOnboardingForm = forwardRef(
  (
    {
      clientId,
      onSuccess,
      onError,
      refreshKey,
      clientDetailData,
      clientDetailLoading,
      clientDetailError,
    },
    ref
  ) => {
    // Obtener filterRefresh del contexto del outlet para que los cambios de filtro disparen re-fetch
    const { filterRefresh } = useOutletContext();
    const { user: currentUser, interceptorReady } = useAuth();
    const { theme, colors } = useTheme();

    // Detección de móvil para vista de cliente
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isClient = currentUser?.role === 'client';
    const isClientMobile = isClient && isMobile;

    // bundle.social connections of the networks listed in Activos Digitales (internal users only).
    const socialClientId = typeof clientId === "object" ? clientId?.id : clientId;
    const social = useSocialConnections(socialClientId, {
      enabled: !isClient,
      onSuccess,
      onError,
    });
    const [searchParams] = useSearchParams();
    // Opens Activos Digitales when coming from "Conectar" or back from the bundle.social portal.
    const openAssetsTab =
      !isClient &&
      (searchParams.get("section") === "activos" ||
        searchParams.get("tab") === "redes" ||
        readPortalReturn(searchParams).length > 0);

    // Estado para secciones expandidas en vista cliente móvil
    const [expandedSections, setExpandedSections] = useState({
      basicos: true,
      fiscales: false,
      documentacion: false,
      activos: false,
    });

    // Estado para los datos del formulario
    const [formData, setFormData] = useState({
      nombreEmpresa: "",
      sector: "",
      packContratado: "",
      nombreContacto: "",
      emailContacto: "",
      telefonoContacto: "",
      direccion: "",
      ciudad: "",
      codigoPostal: "",
      notas: "",
      notas_internas: "",
      website: "",
      digital_assets: [], // Now loaded from backend, empty by default
      razonSocial: "",
      cifNif: "",
      direccionFiscal: "",
      contactoPrincipal: "",
      telefono: "",
      emailFacturacion: "",
      googleDriveLink: "",
      notebooklmLink: "",
      clientifyLink: "",
      todolistLink: "",
      trackingPanelLink: "",
      visibilityPanelLink: "",
    });

    // Estado para validación de campos
    const [emailValid, setEmailValid] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [refreshClientDetail, setRefreshClientDetail] = useState(0);

    // Estado para detectar cambios en el formulario
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalFormData, setOriginalFormData] = useState(null);

    // Estado para controlar qué activos digitales están expandidos

    // Estados para el modal de agregar activo digital
    const [showAddAssetModal, setShowAddAssetModal] = useState(false);
    const [selectedAssetTypes, setSelectedAssetTypes] = useState([]); // Array de tipos seleccionados
    const [modalAssets, setModalAssets] = useState([]); // Array de { type, customLabel?, url, error? }

    const INVALID_URL_MESSAGE =
      "Por favor, introduce una url válida (Ejemplo: https://miweb.com)";
    const DOMAIN_ERROR_MESSAGE =
      "El dominio de la URL debe ser válido (Ejemplo: https://miweb.com)";

    const normalizeUrlWithHttps = (url) => {
      if (!url) return "";
      let normalized = url.trim();
      if (!normalized) return "";
      if (normalized.toLowerCase().startsWith("https://")) {
        return normalized;
      }
      if (normalized.toLowerCase().startsWith("http://")) {
        normalized = normalized.slice("http://".length);
      }
      return `https://${normalized.replace(/^\/\//, "")}`;
    };

    const hasValidDomain = (url) => {
      if (!url) return false;
      const withoutProtocol = url.replace(/^https?:\/\//i, "");
      return withoutProtocol.includes(".");
    };

    const validateUrlFormat = (url) => {
      if (!url?.trim()) return null;
      const normalizedUrl = normalizeUrlWithHttps(url);

      if (!hasValidDomain(normalizedUrl)) {
        return DOMAIN_ERROR_MESSAGE;
      }
      if (!normalizedUrl.match(/^https?:\/\//)) {
        return "El link debe tener el formato https://";
      }
      if (normalizedUrl.length > 300) {
        return "La URL no puede superar los 300 caracteres";
      }
      return null;
    };

    const backendHasUrlError = (data) => {
      const containsUrlError = (value) => {
        if (typeof value === "string") {
          return value.toLowerCase().includes("valid url");
        }
        if (Array.isArray(value)) {
          return value.some((item) => containsUrlError(item));
        }
        if (value && typeof value === "object") {
          return Object.values(value).some((item) => containsUrlError(item));
        }
        return false;
      };

      return containsUrlError(data);
    };

    // Opciones para los selectores
    const sectoresOptions = [
      { value: "restauracion", label: "Restauración" },
      { value: "retail", label: "Retail / Comercio" },
      { value: "servicios", label: "Servicios Profesionales" },
      { value: "salud", label: "Salud y Bienestar" },
      { value: "tecnologia", label: "Tecnología" },
      { value: "educacion", label: "Educación" },
      { value: "inmobiliario", label: "Inmobiliario" },
      { value: "turismo", label: "Turismo y Hostelería" },
      { value: "automocion", label: "Automoción" },
    ];

    const packsOptions = [
      { value: "basic", label: "Pack Basic" },
      { value: "ecommerce", label: "Pack E-commerce" },
      { value: "premium", label: "Pack Premium" },
      { value: "local", label: "Pack Starter" },
      { value: "infoproducto", label: "Pack Infoproducto" },
    ];
    const NO_PACK_SELECTOR_VALUE = "__no_pack__";

    const sugerencias = [
      { key: "facebook", label: "Facebook", Icon: Facebook, color: platformColors.facebook },
      {
        key: "instagram",
        label: "Instagram",
        Icon: Instagram,
        color: platformColors.instagram,
      },
      { key: "linkedin", label: "LinkedIn", Icon: LinkedIn, color: platformColors.linkedin },
      { key: "youtube", label: "YouTube", Icon: YouTube, color: platformColors.youtube },
      { key: "tiktok", label: "TikTok", Icon: TikTokIcon, color: platformColors.tiktok },
      { key: "blog", label: "Blog", Icon: Article, color: platformColors.blog },
      {
        key: "email_marketing",
        label: "Email Marketing",
        Icon: Email,
        color: platformColors.email_marketing,
      },
      {
        key: "google_business_profile",
        label: "Perfil de empresa",
        Icon: GoogleIcon,
        color: platformColors.google_business_profile,
      },
      {
        key: "otros",
        label: "Otros",
        Icon: LinkIcon,
        color: platformColors.others,
      },
    ];

    // Cargar datos del cliente
    // Solo incluir filterRefresh como dependencia para clientes (multicliente)
    const isClientRole = currentUser?.role === 'client';
    const packSelectorOptions = isClientRole
      ? packsOptions
      : [{ value: NO_PACK_SELECTOR_VALUE, label: "Sin pack" }, ...packsOptions];
    const normalizePackSelectorValue = (value) =>
      value === NO_PACK_SELECTOR_VALUE ? "" : value;
    const hasParentClientDetail =
      clientDetailData !== undefined ||
      clientDetailLoading !== undefined ||
      clientDetailError !== undefined;

    const shouldFetchClientDetail =
      !hasParentClientDetail && !!clientId && interceptorReady;

    const {
      data: fetchedClientDetailData,
      error: fetchedClientDetailError,
    } = getData(
      shouldFetchClientDetail ? `clients/${clientId}/` : null,
      shouldFetchClientDetail,
      isClientRole
        ? [clientId, refreshClientDetail, refreshKey, filterRefresh]
        : [clientId, refreshClientDetail, refreshKey]
    );

    const resolvedClientDetailData = hasParentClientDetail
      ? clientDetailData
      : fetchedClientDetailData;
    const resolvedClientDetailError = hasParentClientDetail
      ? clientDetailError
      : fetchedClientDetailError;
    const loadingClientDetail = hasParentClientDetail
      ? !!clientId && clientDetailLoading && !clientDetailError
      : !!clientId && !resolvedClientDetailData && !resolvedClientDetailError;
    const refetchClientData = null;
    const setupVariantEditable =
      resolvedClientDetailData?.setup_variant_editable ?? true;
    // Agencias externas: el pack no se muestra; su Setup se gestiona desde la página de Setup
    const setupVariantVisible =
      resolvedClientDetailData?.setup_variant_visible ?? true;

    // Internos: las redes publicables van en Conexiones; el cliente solo ve Enlaces.
    const showConnections = !isClient && Boolean(socialClientId);
    const linkAssets = showConnections
      ? formData.digital_assets.filter((a) => !isPublishablePlatform(a.channel))
      : formData.digital_assets;

    // URL opcional del perfil de una red en Conexiones (se guarda como activo digital).
    const renderSocialUrlField = (platform) => {
      const asset = formData.digital_assets.find((a) => a.channel === platform);
      const errorKey = `${platform}_url`;
      return (
        <CustomTextField
          id={`url-${platform}`}
          type="url"
          value={asset?.url || ""}
          onInput={() => {
            if (validationErrors[errorKey]) {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
              });
            }
          }}
          onChange={(e) => handleInputChange(`digital_asset_${platform}`, e.target.value, true)}
          placeholder="URL del perfil (opcional)"
          maxLength={300}
          validateUrl={true}
          error={validationErrors[errorKey]}
          errorMessage={validationErrors[errorKey]}
          setValueOnClickAway={true}
        />
      );
    };

    const getAssetInfo = (asset) => {
      if (asset.channel) {
        const canal = sugerencias.find((s) => s.key === asset.channel);
        if (canal) {
          return {
            label: canal.label,
            Icon: canal.Icon,
            color: canal.color,
            isCustom: false,
          };
        }
      }
      if (asset.custom_label) {
        return {
          label: asset.custom_label,
          Icon: LinkIcon,
          color: colors.primary.main,
          isCustom: true,
        };
      }
      return null;
    };

    // Componente para editar campos inline en el modal
    const ModalEditableField = ({
      value,
      onSave,
      placeholder,
      disabled = false,
    }) => {
      const [isEditing, setIsEditing] = useState(false);
      const [editValue, setEditValue] = useState(value);

      React.useEffect(() => {
        setEditValue(value);
      }, [value]);

      const handleSave = () => {
        if (editValue !== value) {
          onSave(editValue);
        }
        setIsEditing(false);
      };

      const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
      };

      if (isEditing) {
        return (
          <Box
            data-prevent-expand="true"
            onClick={(event) => event.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              cursor: "default",
              padding: 0,
              margin: 0,
            }}
          >
            <CustomTextField
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
              autoFocus
              placeholder={placeholder}
              sx={{ flex: 1 }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
                flexShrink: 0,
                paddingTop: "8px",
              }}
              data-prevent-expand="true"
            >
              <IconButton
                size="small"
                data-prevent-expand="true"
                onClick={(event) => {
                  event.stopPropagation();
                  handleSave();
                }}
              >
                <CheckCircle2 size={16} />
              </IconButton>
            </Box>
          </Box>
        );
      }

      return (
        <Box
          data-prevent-expand="true"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            width: "fit-content",
            maxWidth: "100%",
            cursor: disabled ? "default" : "pointer",
            "&:hover": {
              backgroundColor: disabled ? "transparent" : "action.hover",
            },
            padding: "4px 8px",
            borderRadius: "4px",
          }}
          onClick={(event) => {
            if (disabled) {
              return;
            }
            event.stopPropagation();
            setIsEditing(true);
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {value || placeholder ? (
              <Typography
                variant="body2"
                sx={{
                  color: value ? "text.primary" : "text.secondary",
                  whiteSpace: "nowrap",
                  overflow: "visible",
                  wordBreak: "normal",
                  fontWeight: 500,
                }}
              >
                {value || placeholder}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: "text.disabled",
                  fontStyle: "italic",
                }}
              >
                {placeholder || "Sin valor"}
              </Typography>
            )}
          </Box>
          {!disabled && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              sx={{
                opacity: 0.6,
                "&:hover": { opacity: 1 },
                flexShrink: 0,
              }}
              data-prevent-expand="true"
            >
              <Edit size={14} />
            </IconButton>
          )}
        </Box>
      );
    };

    // Sincronizar formData cuando se cargan los datos del cliente
    useEffect(() => {
      if (resolvedClientDetailData) {
        const newFormData = {
          nombreEmpresa: resolvedClientDetailData.name || "",
          sector: resolvedClientDetailData.sector || "",
          packContratado: resolvedClientDetailData.setup_variant || "",
          nombreContacto: resolvedClientDetailData.contact_name || "",
          emailContacto: resolvedClientDetailData.contact_email || "",
          telefonoContacto: resolvedClientDetailData.contact_phone || "",
          direccion: resolvedClientDetailData.address || "",
          ciudad: resolvedClientDetailData.location || "",
          codigoPostal: resolvedClientDetailData.fiscal_postal_code || "",
          notas: resolvedClientDetailData.notes || "",
          notas_internas: resolvedClientDetailData.internal_notes || "",
          website: resolvedClientDetailData.website || "",
          digital_assets: resolvedClientDetailData.digital_assets || [], // Load existing assets from backend
          razonSocial: resolvedClientDetailData.legal_name || "",
          cifNif: resolvedClientDetailData.tax_id || "",
          direccionFiscal: resolvedClientDetailData.fiscal_address || "",
          contactoPrincipal: resolvedClientDetailData.commercial_contact || "",
          telefono: resolvedClientDetailData.commercial_phone || "",
          emailFacturacion: resolvedClientDetailData.billing_email || "",
          googleDriveLink: resolvedClientDetailData.drive_url || "",
          notebooklmLink: resolvedClientDetailData.notebooklm_url || "",
          clientifyLink: resolvedClientDetailData.clientify_url || "",
          todolistLink: resolvedClientDetailData.todolist_url || "",
          trackingPanelLink: resolvedClientDetailData.tracking_panel_url || "",
          visibilityPanelLink:
            resolvedClientDetailData.visibility_panel_url || "",
        };

        setFormData(newFormData);
        // Guardar los datos originales para comparar cambios
        setOriginalFormData(JSON.parse(JSON.stringify(newFormData)));
        setHasUnsavedChanges(false);
      }
    }, [resolvedClientDetailData]);

    // Detectar cambios en el formulario
    useEffect(() => {
      if (originalFormData) {
        const hasChanges =
          JSON.stringify(formData) !== JSON.stringify(originalFormData);
        setHasUnsavedChanges(hasChanges);
      }
    }, [formData, originalFormData]);

    // Exponer funciones al componente padre usando useImperativeHandle
    useImperativeHandle(
      ref,
      () => ({
        saveIfHasChanges: async () => {
          if (hasUnsavedChanges && clientId) {
            try {
              if (validateFields()) {
                await handleSave();
                return {
                  success: true,
                  message: "Datos guardados exitosamente",
                };
              } else {
                return {
                  success: false,
                  message: "Hay errores de validación en el formulario",
                };
              }
            } catch (error) {
              console.error("Error al guardar:", error);
              return { success: false, message: "Error al guardar los datos" };
            }
          }
          return { success: true, message: "No hay cambios que guardar" };
        },
        hasUnsavedChanges: () => hasUnsavedChanges,
      }),
      [hasUnsavedChanges, clientId, formData, validateFields, handleSave]
    );

    const validateField = (field, value, currentFormData) => {
      const errors = {};

      // Validar según el campo específico
      switch (field) {
        case "emailContacto":
          if (value?.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
              errors.emailContacto = "Introduce un correo electrónico válido";
            }
          }
          break;

        case "telefonoContacto":
          if (value && value.length > 20) {
            errors.telefonoContacto =
              "El teléfono no puede superar los 20 caracteres";
          }
          break;

        case "website":
          if (!value?.trim()) {
            errors.website = "Este campo es obligatorio";
          } else {
            const normalizedWebsite = normalizeUrlWithHttps(value);
            if (!hasValidDomain(normalizedWebsite)) {
              errors.website = DOMAIN_ERROR_MESSAGE;
            } else if (!normalizedWebsite.match(/^https?:\/\//)) {
              errors.website = "El link debe tener el formato https://";
            }
            if (normalizedWebsite.length > 300) {
              errors.website = "La URL no puede superar los 300 caracteres";
            }
          }
          break;

        case "googleDriveLink":
        case "notebooklmLink":
        case "clientifyLink":
        case "todolistLink":
        case "trackingPanelLink":
        case "visibilityPanelLink":
          if (value?.trim()) {
            const urlError = validateUrlFormat(value);
            if (urlError) {
              errors[field] = urlError;
            }
          }
          break;

        default:
          // Para digital_assets predefinidos
          if (field.startsWith("digital_asset_")) {
            const channel = field.split("_").slice(2).join("_"); // Obtener el channel (puede tener _ como email_marketing)
            const asset = currentFormData.digital_assets.find(
              (a) => a.channel === channel
            );
            if (asset?.url?.trim()) {
              const urlError = validateUrlFormat(asset.url);
              if (urlError) {
                errors[`${channel}_url`] = urlError;
              }
            }
          }
          // Para digital_assets personalizados
          else if (field.startsWith("custom_asset_")) {
            const customLabel = field.replace("custom_asset_", "");
            const asset = currentFormData.digital_assets.find(
              (a) => a.custom_label === customLabel
            );
            if (asset?.url?.trim()) {
              const urlError = validateUrlFormat(asset.url);
              if (urlError) {
                errors[`custom_${customLabel}_url`] = urlError;
              }
            }
          }
          break;
      }

      return errors;
    };

    // `validateNow`: el campo ya está confirmado (blur o selección); se valida, se guarda con la barra.
    const handleInputChange = (field, value, validateNow = false) => {
      // Manejar cambios en digital_assets predefinidos
      if (field.startsWith("digital_asset_")) {
        const parts = field.split("_");
        const channel = parts.slice(2).join("_"); // Obtener el channel (puede tener _ como email_marketing)

        // Find existing asset or prepare to create new one
        const existingAssetIndex = formData.digital_assets.findIndex(
          (a) => a.channel === channel
        );

        let newFormData;
        if (existingAssetIndex >= 0) {
          // Update existing asset
          newFormData = {
            ...formData,
            digital_assets: formData.digital_assets.map((asset, idx) =>
              idx === existingAssetIndex ? { ...asset, url: value } : asset
            ),
          };
        } else if (value.trim() !== "") {
          // Create new asset (no id, backend will create it)
          newFormData = {
            ...formData,
            digital_assets: [
              ...formData.digital_assets,
              {
                channel: channel,
                url: value,
              },
            ],
          };
        } else {
          newFormData = formData;
        }

        setFormData(newFormData);

        // Validar el campo específico antes de guardar
        if (validateNow) {
          const fieldErrors = validateField(field, value, newFormData);

          if (Object.keys(fieldErrors).length > 0) {
            // Si hay errores, solo mostrarlos en el campo, NO mostrar popup de error
            setValidationErrors((prev) => ({ ...prev, ...fieldErrors }));
            return; // No guardar si hay errores
          }

          // Limpiar errores si la validación pasó
          const errorKey = `${channel}_url`;
          if (validationErrors[errorKey]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[errorKey];
              return newErrors;
            });
          }

        } else {
          const errorKey = `${channel}_url`;
          if (validationErrors[errorKey]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[errorKey];
              return newErrors;
            });
          }
        }
      }
      // Manejar cambios en custom assets
      else if (field.startsWith("custom_asset_")) {
        const customLabel = field.replace("custom_asset_", "");

        const existingAssetIndex = formData.digital_assets.findIndex(
          (a) => a.custom_label === customLabel
        );

        let newFormData;
        if (existingAssetIndex >= 0) {
          // Update existing asset
          newFormData = {
            ...formData,
            digital_assets: formData.digital_assets.map((asset, idx) =>
              idx === existingAssetIndex ? { ...asset, url: value } : asset
            ),
          };
        } else if (value.trim() !== "") {
          // Create new asset
          newFormData = {
            ...formData,
            digital_assets: [
              ...formData.digital_assets,
              {
                custom_label: customLabel,
                url: value,
              },
            ],
          };
        } else {
          newFormData = formData;
        }

        setFormData(newFormData);

        // Validar el campo específico antes de guardar
        if (validateNow) {
          const fieldErrors = validateField(field, value, newFormData);

          if (Object.keys(fieldErrors).length > 0) {
            setValidationErrors((prev) => ({ ...prev, ...fieldErrors }));
            return;
          }

          // Validar todos los activos digitales antes de guardar
          const assetErrors = validateDigitalAssets(newFormData);

          if (Object.keys(assetErrors).length > 0) {
            // Mostrar errores en todos los activos digitales
            setValidationErrors((prev) => ({ ...prev, ...assetErrors }));
            return; // No guardar si hay errores en cualquier activo digital
          }

          // Limpiar errores de activos digitales si la validación pasó
          const assetErrorKeys = Object.keys(validationErrors).filter((key) =>
            key.includes("_url")
          );
          if (assetErrorKeys.length > 0) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              assetErrorKeys.forEach((key) => delete newErrors[key]);
              return newErrors;
            });
          }
        } else {
          const errorKey = `custom_${customLabel}_url`;
          if (validationErrors[errorKey]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[errorKey];
              return newErrors;
            });
          }
        }
      } else {
        const newFormData = { ...formData, [field]: value };
        setFormData(newFormData);

        // Validar el campo específico antes de guardar
        if (validateNow) {
          const fieldErrors = validateField(field, value, newFormData);

          if (Object.keys(fieldErrors).length > 0) {
            // Si hay errores, solo mostrarlos en el campo, NO mostrar popup de error
            setValidationErrors((prev) => ({ ...prev, ...fieldErrors }));
            return; // No guardar si hay errores
          }

          // Limpiar errores si la validación pasó
          if (validationErrors[field]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[field];
              return newErrors;
            });
          }
        } else {
          if (validationErrors[field]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[field];
              return newErrors;
            });
          }
        }
      }
    };

    const handleEmailValidation = (isValid) => {
      setEmailValid(isValid);
    };

    const handleRemoveCustomChannel = async (channelName) => {
      const asset = formData.digital_assets.find(
        (a) => a.custom_label === channelName
      );
      if (asset) {
        await handleRemoveAsset(asset);
      }
    };

    const handleRemoveAsset = async (asset) => {
      const previousFormData = formData;
      const updatedAssets = formData.digital_assets.filter((a) => a !== asset);
      const newFormData = {
        ...formData,
        digital_assets: updatedAssets,
      };

      setFormData(newFormData);

      // Limpiar errores de validación
      const errorKey = asset.channel
        ? `${asset.channel}_url`
        : `custom_${asset.custom_label}_url`;
      if (validationErrors[errorKey]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }

      // Si el activo existe en backend, llamamos al endpoint de borrado
      if (asset.id) {
        if (!clientId) {
          if (onError)
            onError("No hay cliente seleccionado para eliminar el activo");
          return;
        }

        try {
          await deleteClientDigitalAsset(clientId, asset.id);
          // Sincronizar el estado original para que no quede como cambio pendiente
          setOriginalFormData((prev) => {
            if (!prev) return prev;
            const filteredOriginalAssets = (prev.digital_assets || []).filter(
              (a) => a.id !== asset.id
            );
            return { ...prev, digital_assets: filteredOriginalAssets };
          });
          if (onSuccess) onSuccess("Activo digital eliminado");
        } catch (err) {
          console.error("Error eliminando activo digital:", err);
          // Revertir UI si falla el borrado
          setFormData(previousFormData);
          if (onError) {
            if (err.response?.status === 404) {
              onError("El activo digital no existe o no pertenece al cliente");
            } else if (err.response?.status === 403) {
              onError("No tiene permiso para eliminar este activo");
            } else {
              onError("Error al eliminar el activo digital");
            }
          }
          return;
        }
      } else {
        // Activo aún no persistido (o recién creado pero sin id)
        if (onSuccess) onSuccess("Activo digital eliminado");
      }
    };

    const handleOpenAddAssetModal = () => {
      setShowAddAssetModal(true);
      setSelectedAssetTypes([]);
      setModalAssets([]);
    };

    const handleCloseAddAssetModal = () => {
      setShowAddAssetModal(false);
      setSelectedAssetTypes([]);
      setModalAssets([]);
    };

    const handleToggleAssetType = (typeKey) => {
      if (typeKey === "otros") {
        // Para "otros", siempre agregar uno nuevo
        const newOtrosId = `otros_${Date.now()}`;
        setSelectedAssetTypes((prev) => [...prev, newOtrosId]);
        setModalAssets((prev) => [
          ...prev,
          {
            id: newOtrosId,
            type: "otros",
            customLabel: "",
            url: "",
            error: "",
          },
        ]);
      } else {
        // Para tipos predefinidos
        if (selectedAssetTypes.includes(typeKey)) {
          // Deseleccionar
          setSelectedAssetTypes((prev) => prev.filter((t) => t !== typeKey));
          setModalAssets((prev) => prev.filter((a) => a.type !== typeKey));
        } else {
          // Seleccionar
          setSelectedAssetTypes((prev) => [...prev, typeKey]);
          setModalAssets((prev) => [
            ...prev,
            {
              type: typeKey,
              url: "",
              error: "",
            },
          ]);
        }
      }
    };

    const handleModalAssetUrlChange = (assetIdentifier, url) => {
      setModalAssets((prev) =>
        prev.map((a) => {
          const currentId = a.id || a.type;
          if (currentId === assetIdentifier) {
            return { ...a, url, error: "" };
          }
          return a;
        })
      );
    };

    const handleModalAssetCustomLabelChange = (assetId, customLabel) => {
      setModalAssets((prev) =>
        prev.map((a) => {
          if (a.id === assetId) {
            return { ...a, customLabel };
          }
          return a;
        })
      );
    };

    const handleRemoveModalAsset = (assetIdentifier) => {
      setModalAssets((prev) =>
        prev.filter((a) => {
          const currentId = a.id || a.type;
          return currentId !== assetIdentifier;
        })
      );
      setSelectedAssetTypes((prev) =>
        prev.filter((t) => t !== assetIdentifier)
      );
    };

    const validateModalAssets = () => {
      let hasErrors = false;
      const updatedAssets = modalAssets.map((asset) => {
        let error = "";

        // Validar label personalizado para "otros"
        if (asset.type === "otros" && !asset.customLabel?.trim()) {
          error = "Debes introducir un nombre para el canal personalizado";
          hasErrors = true;
        }

        // Validar URL
        if (!asset.url?.trim()) {
          error = "Por favor, introduce una URL";
          hasErrors = true;
        } else {
          const urlError = validateUrlFormat(asset.url);
          if (urlError) {
            error = urlError;
            hasErrors = true;
          }
        }

        return { ...asset, error };
      });

      setModalAssets(updatedAssets);
      return !hasErrors;
    };

    const handleConfirmAddAsset = async () => {
      // Validar todos los assets del modal
      if (!validateModalAssets()) {
        return;
      }

      // Crear los nuevos assets
      const newAssets = modalAssets.map((modalAsset) => {
        const normalizedUrl = modalAsset.url?.trim()
          ? normalizeUrlWithHttps(modalAsset.url)
          : "";
        if (modalAsset.type === "otros") {
          return {
            custom_label: modalAsset.customLabel.trim(),
            url: normalizedUrl,
          };
        } else {
          return {
            channel: modalAsset.type,
            url: normalizedUrl,
          };
        }
      });

      // Agregar al formData
      const newFormData = {
        ...formData,
        digital_assets: [...formData.digital_assets, ...newAssets],
      };

      // Guardar
      await handleSave(newFormData);

      // Cerrar modal
      handleCloseAddAssetModal();
    };

    const getAssetTypeOptions = () => {
      // Filtrar las opciones que ya no están disponibles (ya agregadas)
      return sugerencias
        .filter((s) => {
          if (s.key === "otros") return true; // Siempre mostrar "Otros"
          if (showConnections && isPublishablePlatform(s.key)) return false; // Se conectan en Conexiones
          return !formData.digital_assets.some((a) => a.channel === s.key);
        })
        .map((s) => ({
          value: s.key,
          label: s.label,
          Icon: s.Icon,
          color: s.color,
        }));
    };

    const validateFields = () => {
      const errors = {};

      if (formData.emailContacto?.trim()) {
        if (!formData.emailContacto.includes("@") || !emailValid) {
          errors.emailContacto = "Introduce un correo electrónico válido";
        }
      }

      if (formData.telefonoContacto && formData.telefonoContacto.length > 20) {
        errors.telefonoContacto = "Campo demasiado largo";
      }

      // Website ya no es obligatorio, solo validar formato si tiene contenido
      if (formData.website?.trim()) {
        const normalizedWebsite = normalizeUrlWithHttps(formData.website);
        if (!hasValidDomain(normalizedWebsite)) {
          errors.website = DOMAIN_ERROR_MESSAGE;
        } else if (!normalizedWebsite.match(/^https?:\/\//)) {
          errors.website = "El link debe tener el formato https://";
        }
      }

      // Validar longitud de website
      if (
        formData.website &&
        normalizeUrlWithHttps(formData.website).length > 300
      ) {
        errors.website = "Campo demasiado largo";
      }

      [
        "googleDriveLink",
        "notebooklmLink",
        "clientifyLink",
        "todolistLink",
        "trackingPanelLink",
        "visibilityPanelLink",
      ].forEach((field) => {
        const value = formData[field];
        if (value?.trim()) {
          const urlError = validateUrlFormat(value);
          if (urlError) {
            errors[field] = urlError;
          }
        }
      });

      // Validar formato y longitud de URLs de digital_assets
      const assetErrors = validateDigitalAssets(formData);
      Object.assign(errors, assetErrors);

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const validateDigitalAssets = (currentFormData) => {
      const errors = {};

      currentFormData.digital_assets.forEach((asset) => {
        if (asset.url?.trim()) {
          const errorKey = asset.channel
            ? `${asset.channel}_url`
            : `custom_${asset.custom_label}_url`;

          const urlError = validateUrlFormat(asset.url);
          if (urlError) {
            errors[errorKey] = urlError;
          }
        }
      });

      return errors;
    };

    const handleSave = async (dataToUse = null) => {
      if (!clientId) {
        if (onError) onError("No hay cliente seleccionado para guardar");
        return { ok: false, reason: "missing_client" };
      }

      // Usar los datos pasados como parámetro o los del estado
      const currentFormData = dataToUse || formData;

      if (!validateFields()) {
        return { ok: false, reason: "validation" };
      }

      setIsSaving(true);

      try {
        // Preparar digital_assets para actualización según nuevo formato del backend
        // Solo incluir assets que tienen URL (los demás no se envían)
        const digitalAssetsToUpdate = currentFormData.digital_assets
          .filter((asset) => asset.url && asset.url.trim() !== "") // Solo assets con URL
          .map((asset) => {
            const normalizedUrl = normalizeUrlWithHttps(asset.url);
            const assetData = {
              channel: asset.channel,
              url: normalizedUrl || null,
            };
            // Si el asset ya existe (tiene id), incluirlo para actualizar
            if (asset.id) {
              assetData.id = asset.id;
            }
            // Si tiene custom_label en lugar de channel
            if (asset.custom_label) {
              delete assetData.channel;
              assetData.custom_label = asset.custom_label;
            }
            return assetData;
          });

        const dataToSend = {
          name: currentFormData.nombreEmpresa,
          sector: currentFormData.sector,
          contact_name: currentFormData.nombreContacto,
          contact_email: currentFormData.emailContacto,
          contact_phone: currentFormData.telefonoContacto,
          address: currentFormData.direccion,
          location: currentFormData.ciudad,
          fiscal_postal_code: currentFormData.codigoPostal,
          notes: currentFormData.notas,
          internal_notes: currentFormData.notas_internas,
          website: currentFormData.website?.trim()
            ? normalizeUrlWithHttps(currentFormData.website)
            : "",
          digital_assets: digitalAssetsToUpdate,
          legal_name: currentFormData.razonSocial,
          tax_id: currentFormData.cifNif,
          fiscal_address: currentFormData.direccionFiscal,
          primary_contact: currentFormData.contactoPrincipal,
          phone: currentFormData.telefono,
          billing_email: currentFormData.emailFacturacion,
          drive_url: currentFormData.googleDriveLink?.trim()
            ? normalizeUrlWithHttps(currentFormData.googleDriveLink)
            : "",
          notebooklm_url: currentFormData.notebooklmLink?.trim()
            ? normalizeUrlWithHttps(currentFormData.notebooklmLink)
            : "",
          clientify_url: currentFormData.clientifyLink?.trim()
            ? normalizeUrlWithHttps(currentFormData.clientifyLink)
            : "",
          todolist_url: currentFormData.todolistLink?.trim()
            ? normalizeUrlWithHttps(currentFormData.todolistLink)
            : "",
          tracking_panel_url: currentFormData.trackingPanelLink?.trim()
            ? normalizeUrlWithHttps(currentFormData.trackingPanelLink)
            : "",
          visibility_panel_url: currentFormData.visibilityPanelLink?.trim()
            ? normalizeUrlWithHttps(currentFormData.visibilityPanelLink)
            : "",
        };

        const originalPackContratado = originalFormData?.packContratado || "";
        if (
          Object.prototype.hasOwnProperty.call(currentFormData, "packContratado") &&
          currentFormData.packContratado !== originalPackContratado
        ) {
          dataToSend.setup_variant = currentFormData.packContratado || "";
        }

        const response = await patchWithRefresh(
          `clients/${clientId}/`,
          dataToSend,
          // Solo hacer refetch si NO estamos en modo autoguardado
          dataToUse ? null : refetchClientData
        );

        if (response.data) {
          // Actualizar formData con la respuesta del backend para sincronizar los IDs de los assets creados
          const updatedFormData = {
            ...currentFormData,
            digital_assets: response.data.digital_assets || [],
          };
          setFormData(updatedFormData);

          // Actualizar los datos originales después del guardado exitoso
          setOriginalFormData(JSON.parse(JSON.stringify(updatedFormData)));
          setHasUnsavedChanges(false);
          // Mostrar siempre el popup de éxito, tanto en autoguardado como en guardado manual
          if (onSuccess) onSuccess("Datos guardados exitosamente");
          return { ok: true };
        }
      } catch (err) {
        console.error("Error guardando datos:", err);
        const backendData = err.response?.data;
        if (backendHasUrlError(backendData)) {
          if (onError) onError(INVALID_URL_MESSAGE);
        } else if (err.response && err.response.status === 403) {
          if (onError) onError("No tiene permiso para esta acción");
        } else {
          if (onError)
            onError(
              err.response?.data?.message ||
              "Error al guardar los datos del cliente"
            );
        }
        return { ok: false, reason: "backend" };
      } finally {
        setIsSaving(false);
      }
    };

    // Función para guardar automáticamente al cambiar de pestaña
    const handleTabChange = async (newTabValue) => {
      if (hasUnsavedChanges && clientId) {
        // Intentar guardar antes de cambiar de pestaña
        try {
          if (validateFields()) {
            await handleSave();
          }
        } catch (error) {
          console.error("Error al guardar automáticamente:", error);
          // Continuar con el cambio de pestaña aunque falle el guardado
        }
      }
    };

    if (loadingClientDetail) {
      return (
        <Card
          sx={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              padding: 6,
              textAlign: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress />
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Cargando datos del cliente...
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const unsavedChanges = originalFormData
      ? Object.keys(formData).filter((key) => JSON.stringify(formData[key]) !== JSON.stringify(originalFormData[key])).length
      : 0;
    const unsavedBar = (
      <UnsavedChangesBar
        count={unsavedChanges}
        saving={isSaving}
        onDiscard={() => {
          setFormData(JSON.parse(JSON.stringify(originalFormData)));
          setValidationErrors({});
        }}
        onSave={() => handleSave()}
      />
    );

    // Vista móvil para cliente - Secciones colapsables verticales
    if (isClientMobile) {
      return (
        <Card
          sx={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: 2,
              height: "100%",
              overflowY: "auto",
              gap: 2,
            }}
          >
            {/* Sección 1: Datos Básicos */}
            <Collapsible>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <CollapsibleTrigger
                  onClick={() =>
                    setExpandedSections((prev) => ({
                      ...prev,
                      basicos: !prev.basicos,
                    }))
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      backgroundColor: expandedSections.basicos
                        ? `${colors.primary.main}08`
                        : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: brand.inkFill,
                          color: brand.inkFillText,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        1
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Datos Básicos
                      </Typography>
                    </Box>
                    <ChevronDown
                      size={20}
                      style={{
                        transform: expandedSections.basicos
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </Box>
                </CollapsibleTrigger>
                <CollapsibleContent open={expandedSections.basicos}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      padding: 2,
                      paddingTop: 0,
                    }}
                  >
                    {/* Nombre de la empresa - Fila completa */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        pt: 0.5,
                      }}
                    >
                      <Label htmlFor="nombreEmpresa">
                        Nombre de la Empresa
                      </Label>
                      <CustomTextField
                        id="nombreEmpresa"
                        value={formData.nombreEmpresa}
                        onChange={(e) =>
                          handleInputChange(
                            "nombreEmpresa",
                            e.target.value,
                            true
                          )
                        }
                        placeholder="Ej: Restaurante La Buena Mesa"
                        setValueOnClickAway={true}
                      />
                    </Box>

                    {/* Sector y Pack - 2 por fila */}
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Label htmlFor="sector">Sector</Label>
                          <CustomSelector
                            value={formData.sector}
                            onChange={(value) => {
                              if (value !== formData.sector) {
                                handleInputChange("sector", value, true);
                              }
                            }}
                            options={sectoresOptions}
                            placeholder="Seleccionar"
                            searchable
                            freeSolo
                          />
                        </Box>
                      </Grid>
                      {setupVariantVisible && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {!isClient ? (
                            <InfoTooltip title="Al contratar un pack se crea automáticamente un proyecto de setup, que seguirá la plantilla del pack seleccionado. Una vez iniciado el proyecto de setup, ya no será posible modificar el pack.">
                              <Label htmlFor="packContratado" sx={{ cursor: "help" }}>Pack</Label>
                            </InfoTooltip>
                          ) : (
                            <Label htmlFor="packContratado">Pack</Label>
                          )}
                          {(() => {
                            const selector = (
                              <CustomSelector
                                value={formData.packContratado}
                                onChange={(value) => {
                                  const normalizedValue =
                                    normalizePackSelectorValue(value);
                                  if (normalizedValue !== formData.packContratado) {
                                    handleInputChange(
                                      "packContratado",
                                      normalizedValue,
                                      true
                                    );
                                  }
                                }}
                                options={packSelectorOptions}
                                placeholder="Selecciona el pack"
                                disabled={!setupVariantEditable}
                              />
                            );

                            if (!setupVariantEditable && !isClient) {
                              const packLabel = packSelectorOptions.find(p => p.value === formData.packContratado)?.label || formData.packContratado || '';
                              return (
                                <Tooltip
                                  title={`El setup con la plantilla ${packLabel} ya tiene fases/tareas completadas. no es posible modificarlo.`}
                                  arrow
                                  placement="bottom"
                                >
                                  <Box sx={{ cursor: "not-allowed", width: "100%" }}>
                                    <Box sx={{ pointerEvents: "none", width: "100%" }}>
                                      {selector}
                                    </Box>
                                  </Box>
                                </Tooltip>
                              );
                            }

                            return selector;
                          })()}
                        </Box>
                      </Grid>
                      )}
                    </Grid>

                    <Separator />

                    {/* Nombre contacto - Fila completa */}
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Label htmlFor="nombreContacto">
                        Nombre del Contacto
                      </Label>
                      <CustomTextField
                        id="nombreContacto"
                        value={formData.nombreContacto}
                        onChange={(e) =>
                          handleInputChange(
                            "nombreContacto",
                            e.target.value,
                            true
                          )
                        }
                        placeholder="Ej: Juan Pérez"
                        setValueOnClickAway={true}
                      />
                    </Box>

                    {/* Teléfono y Email - 2 por fila */}
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Phone
                              size={14}
                              style={{ color: theme.palette.text.secondary }}
                            />
                            <Label htmlFor="telefonoContacto">Teléfono</Label>
                          </Box>
                          <CustomTextField
                            id="telefonoContacto"
                            type="tel"
                            value={formData.telefonoContacto}
                            onChange={(e) =>
                              handleInputChange(
                                "telefonoContacto",
                                e.target.value,
                                true
                              )
                            }
                            placeholder="+34 123 456 789"
                            maxLength={20}
                            error={validationErrors.telefonoContacto}
                            errorMessage={validationErrors.telefonoContacto}
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Mail
                              size={14}
                              style={{ color: theme.palette.text.secondary }}
                            />
                            <Label htmlFor="emailContacto">Email</Label>
                          </Box>
                          <CustomTextField
                            id="emailContacto"
                            type="email"
                            value={formData.emailContacto}
                            onChange={(e) =>
                              handleInputChange(
                                "emailContacto",
                                e.target.value,
                                true
                              )
                            }
                            onValidationChange={handleEmailValidation}
                            placeholder="email@empresa.com"
                            error={validationErrors.emailContacto}
                            errorMessage={validationErrors.emailContacto}
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <Separator />

                    {/* Dirección - Fila completa */}
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <MapPin
                          size={14}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Label htmlFor="direccion">Dirección</Label>
                      </Box>
                      <CustomTextField
                        id="direccion"
                        value={formData.direccion}
                        onChange={(e) =>
                          handleInputChange("direccion", e.target.value, true)
                        }
                        placeholder="Calle, número, piso..."
                        setValueOnClickAway={true}
                      />
                    </Box>

                    {/* Ciudad y CP - 2 por fila */}
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Label htmlFor="ciudad">Ciudad</Label>
                          <CustomTextField
                            id="ciudad"
                            value={formData.ciudad}
                            onChange={(e) =>
                              handleInputChange("ciudad", e.target.value, true)
                            }
                            placeholder="Madrid"
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Label htmlFor="codigoPostal">Código Postal</Label>
                          <CustomTextField
                            id="codigoPostal"
                            value={formData.codigoPostal}
                            onChange={(e) =>
                              handleInputChange(
                                "codigoPostal",
                                e.target.value,
                                true
                              )
                            }
                            placeholder="28001"
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Notas - Fila completa */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Label htmlFor="notas">Notas</Label>
                      <CustomTextArea
                        id="notas"
                        rows={2}
                        value={formData.notas}
                        onChange={(e) =>
                          handleInputChange("notas", e.target.value, true)
                        }
                        placeholder="Notas adicionales..."
                        setValueOnClickAway={true}
                      />
                    </Box>

                    {!isClientRole && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Label htmlFor="notas_internas">Notas Internas (ocultas al cliente)</Label>
                        <CustomTextArea
                          id="notas_internas"
                          rows={2}
                          value={formData.notas_internas}
                          onChange={(e) =>
                            handleInputChange("notas_internas", e.target.value, true)
                          }
                          placeholder="Notas internas (ocultas al cliente)..."
                          setValueOnClickAway={true}
                        />
                      </Box>
                    )}
                  </Box>
                </CollapsibleContent>
              </Box>
            </Collapsible>

            {/* Sección 2: Datos Fiscales */}
            <Collapsible>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <CollapsibleTrigger
                  onClick={() =>
                    setExpandedSections((prev) => ({
                      ...prev,
                      fiscales: !prev.fiscales,
                    }))
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      backgroundColor: expandedSections.fiscales
                        ? `${colors.primary.main}08`
                        : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: brand.inkFill,
                          color: brand.inkFillText,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        2
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Datos Fiscales
                      </Typography>
                    </Box>
                    <ChevronDown
                      size={20}
                      style={{
                        transform: expandedSections.fiscales
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </Box>
                </CollapsibleTrigger>
                <CollapsibleContent open={expandedSections.fiscales}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      padding: 1,
                      paddingTop: 0,
                    }}
                  >
                    {/* Razón Social y CIF - 2 por fila */}
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            pt: 0.5,
                          }}
                        >
                          <Label htmlFor="razonSocial">Razón Social</Label>
                          <CustomTextField
                            id="razonSocial"
                            value={formData.razonSocial}
                            onChange={(e) =>
                              handleInputChange(
                                "razonSocial",
                                e.target.value,
                                true
                              )
                            }
                            placeholder="Razón Social"
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            pt: 0.5,
                          }}
                        >
                          <Label htmlFor="cifNif">CIF/NIF</Label>
                          <CustomTextField
                            id="cifNif"
                            value={formData.cifNif}
                            onChange={(e) =>
                              handleInputChange("cifNif", e.target.value, true)
                            }
                            placeholder="CIF/NIF"
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Dirección Fiscal - Fila completa */}
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <MapPin
                          size={14}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Label htmlFor="direccionFiscal">
                          Dirección Fiscal
                        </Label>
                      </Box>
                      <CustomTextField
                        id="direccionFiscal"
                        value={formData.direccionFiscal}
                        onChange={(e) =>
                          handleInputChange(
                            "direccionFiscal",
                            e.target.value,
                            true
                          )
                        }
                        placeholder="Dirección Fiscal Completa"
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Box>
                </CollapsibleContent>
              </Box>
            </Collapsible>

            {/* Sección 3: Documentación */}
            <Collapsible>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <CollapsibleTrigger
                  onClick={() =>
                    setExpandedSections((prev) => ({
                      ...prev,
                      documentacion: !prev.documentacion,
                    }))
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      backgroundColor: expandedSections.documentacion
                        ? `${colors.primary.main}08`
                        : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: brand.inkFill,
                          color: brand.inkFillText,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        3
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Documentación
                      </Typography>
                    </Box>
                    <ChevronDown
                      size={20}
                      style={{
                        transform: expandedSections.documentacion
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </Box>
                </CollapsibleTrigger>
                <CollapsibleContent open={expandedSections.documentacion}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      padding: 2,
                      paddingTop: 0,
                    }}
                  >
                    {/* Google Drive - Fila completa */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pt: 0.5 }}>
                        <GoogleDriveIcon size={16} style={{ color: theme.palette.text.secondary }} />
                        <Label htmlFor="googleDriveLink">Google Drive</Label>
                      </Box>
                      <CustomTextField
                        id="googleDriveLink"
                        type="url"
                        value={formData.googleDriveLink}
                        disabled={isClient}
                        onInput={(e) => {
                          if (validationErrors.googleDriveLink) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.googleDriveLink;
                              return newErrors;
                            });
                          }
                        }}
                        onChange={(e) => {
                          handleInputChange("googleDriveLink", e.target.value, true);
                        }}
                        placeholder="https://drive.google.com/..."
                        maxLength={300}
                        validateUrl={true}
                        error={validationErrors.googleDriveLink}
                        errorMessage={validationErrors.googleDriveLink}
                        setValueOnClickAway={true}
                      />
                    </Box>

                    {/* NotebookLM - Fila completa */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <NotebookPen size={14} style={{ color: theme.palette.text.secondary }} />
                        <Label htmlFor="notebooklmLink">NotebookLM</Label>
                      </Box>
                      <CustomTextField
                        id="notebooklmLink"
                        type="url"
                        value={formData.notebooklmLink}
                        disabled={isClient}
                        onInput={(e) => {
                          if (validationErrors.notebooklmLink) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.notebooklmLink;
                              return newErrors;
                            });
                          }
                        }}
                        onChange={(e) => {
                          handleInputChange("notebooklmLink", e.target.value, true);
                        }}
                        placeholder="https://notebooklm.google.com/..."
                        maxLength={300}
                        validateUrl={true}
                        error={validationErrors.notebooklmLink}
                        errorMessage={validationErrors.notebooklmLink}
                        setValueOnClickAway={true}
                      />
                    </Box>

                    {/* Grid con enlaces de documentación */}
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <ChecklistRtl sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                            <Label htmlFor="todolistLink">Accesos y credenciales</Label>
                          </Box>
                          <CustomTextField
                            id="todolistLink"
                            type="url"
                            value={formData.todolistLink}
                            disabled={isClient}
                            onInput={(e) => {
                              if (validationErrors.todolistLink) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.todolistLink;
                                  return newErrors;
                                });
                              }
                            }}
                            onChange={(e) => {
                              handleInputChange("todolistLink", e.target.value, true);
                            }}
                            placeholder="https://todolist.com/..."
                            maxLength={300}
                            validateUrl={true}
                            error={validationErrors.todolistLink}
                            errorMessage={validationErrors.todolistLink}
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Activity size={16} style={{ color: theme.palette.text.secondary }} />
                            <Label htmlFor="trackingPanelLink">Panel de Seguimiento</Label>
                          </Box>
                          <CustomTextField
                            id="trackingPanelLink"
                            type="url"
                            value={formData.trackingPanelLink}
                            disabled={isClient}
                            onInput={(e) => {
                              if (validationErrors.trackingPanelLink) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.trackingPanelLink;
                                  return newErrors;
                                });
                              }
                            }}
                            onChange={(e) => {
                              handleInputChange("trackingPanelLink", e.target.value, true);
                            }}
                            placeholder="https://tracking-panel.com/..."
                            maxLength={300}
                            validateUrl={true}
                            error={validationErrors.trackingPanelLink}
                            errorMessage={validationErrors.trackingPanelLink}
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Activity size={16} style={{ color: theme.palette.text.secondary }} />
                            <Label htmlFor="visibilityPanelLink">Panel de Visibilidad</Label>
                          </Box>
                          <CustomTextField
                            id="visibilityPanelLink"
                            type="url"
                            value={formData.visibilityPanelLink}
                            disabled={isClient}
                            onInput={(e) => {
                              if (validationErrors.visibilityPanelLink) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.visibilityPanelLink;
                                  return newErrors;
                                });
                              }
                            }}
                            onChange={(e) => {
                              handleInputChange("visibilityPanelLink", e.target.value, true);
                            }}
                            placeholder="https://visibility-panel.com/..."
                            maxLength={300}
                            validateUrl={true}
                            error={validationErrors.visibilityPanelLink}
                            errorMessage={validationErrors.visibilityPanelLink}
                            setValueOnClickAway={true}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </CollapsibleContent>
              </Box>
            </Collapsible>

            {/* Sección 4: Activos Digitales */}
            <Collapsible>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <CollapsibleTrigger
                  onClick={() =>
                    setExpandedSections((prev) => ({
                      ...prev,
                      activos: !prev.activos,
                    }))
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      backgroundColor: expandedSections.activos
                        ? `${colors.primary.main}08`
                        : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: brand.inkFill,
                          color: brand.inkFillText,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        4
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Activos Digitales
                      </Typography>
                    </Box>
                    <ChevronDown
                      size={20}
                      style={{
                        transform: expandedSections.activos
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </Box>
                </CollapsibleTrigger>
                <CollapsibleContent open={expandedSections.activos}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      padding: 2,
                      paddingTop: 0,
                    }}
                  >
                    {/* Website */}
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          pt: 0.5,
                        }}
                      >
                        <Language
                          sx={{
                            fontSize: 16,
                            color: theme.palette.text.secondary,
                          }}
                        />
                        <Label htmlFor="website">Página Web</Label>
                      </Box>
                      <CustomTextField
                        id="website"
                        type="url"
                        value={formData.website}
                        onInput={(e) => {
                          if (validationErrors.website) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.website;
                              return newErrors;
                            });
                          }
                        }}
                        onChange={(e) => {
                          handleInputChange("website", e.target.value, true);
                        }}
                        placeholder="https://ejemplo.com"
                        maxLength={300}
                        validateUrl={true}
                        error={validationErrors.website}
                        errorMessage={validationErrors.website}
                        setValueOnClickAway={true}
                        isUrlField={true}
                      />
                    </Box>

                    {/* Lista de activos digitales existentes */}
                    {formData.digital_assets.map((asset) => {
                      const info = getAssetInfo(asset);
                      if (!info) return null;
                      const IconComp = info.Icon;
                      const fieldError =
                        validationErrors[
                        asset.channel
                          ? `${asset.channel}_url`
                          : `custom_${asset.custom_label}_url`
                        ];

                      return (
                        <Box
                          key={asset.id || asset.channel || asset.custom_label}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "8px",
                            padding: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <IconComp
                              sx={{ fontSize: 16, color: info.color }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, flex: 1 }}
                            >
                              {info.label}
                            </Typography>
                          </Box>
                          <CustomTextField
                            id={`url-${asset.channel || asset.custom_label}`}
                            type="url"
                            value={asset.url || ""}
                            onInput={(e) => {
                              const errorKey = asset.channel
                                ? `${asset.channel}_url`
                                : `custom_${asset.custom_label}_url`;
                              if (validationErrors[errorKey]) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors[errorKey];
                                  return newErrors;
                                });
                              }
                            }}
                            onChange={(e) => {
                              const fieldName = asset.channel
                                ? `digital_asset_${asset.channel}`
                                : `custom_asset_${asset.custom_label}`;
                              handleInputChange(
                                fieldName,
                                e.target.value,
                                true
                              );
                            }}
                            placeholder={`https://${asset.channel || "ejemplo"
                              }.com`}
                            maxLength={300}
                            validateUrl={true}
                            error={fieldError}
                            errorMessage={fieldError}
                            setValueOnClickAway={true}
                            isUrlField={true}
                          />
                        </Box>
                      );
                    })}

                    {/* Botón agregar activo */}
                    <Box
                      sx={{
                        border: `2px dashed ${theme.palette.divider}`,
                        borderRadius: "8px",
                        padding: 1.5,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        "&:hover": {
                          borderColor: colors.primary.main,
                          backgroundColor: `${colors.primary.main}05`,
                        },
                      }}
                      onClick={handleOpenAddAssetModal}
                    >
                      <Plus
                        size={16}
                        style={{ color: theme.palette.text.secondary }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Agregar activo digital
                      </Typography>
                    </Box>
                  </Box>
                </CollapsibleContent>
              </Box>
            </Collapsible>
          </CardContent>

          {/* Modal para agregar activo digital - Reutilizado */}
          <UnifiedModal
            open={showAddAssetModal}
            onClose={handleCloseAddAssetModal}
            title="Agregar Activos Digitales"
            maxWidth="md"
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Label>Selecciona los activos que quieres agregar</Label>
                <Grid container spacing={1}>
                  {getAssetTypeOptions().map((option) => {
                    const IconComp = option.Icon;
                    const isSelected =
                      selectedAssetTypes.includes(option.value) ||
                      (option.value === "otros" &&
                        selectedAssetTypes.some((t) => t.startsWith("otros_")));

                    return (
                      <Grid key={option.value} size={{ xs: 6 }}>
                        <Box
                          onClick={() => handleToggleAssetType(option.value)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            padding: 1,
                            borderRadius: "8px",
                            border: `1px solid ${isSelected
                              ? colors.primary.main
                              : theme.palette.divider
                              }`,
                            backgroundColor: isSelected
                              ? `${colors.primary.main}08`
                              : "transparent",
                            cursor: "pointer",
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() =>
                              handleToggleAssetType(option.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          <IconComp
                            sx={{ fontSize: 18, color: option.color }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.8rem" }}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {modalAssets.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Separator />
                  <Label>Completa la información</Label>
                  {modalAssets.map((modalAsset) => {
                    const assetId = modalAsset.id || modalAsset.type;
                    let assetInfo;
                    if (modalAsset.type === "otros") {
                      assetInfo = {
                        label: modalAsset.customLabel || "Otros",
                        Icon: LinkIcon,
                        color: colors.primary.main,
                      };
                    } else {
                      const suggestion = sugerencias.find(
                        (s) => s.key === modalAsset.type
                      );
                      if (suggestion) {
                        assetInfo = {
                          label: suggestion.label,
                          Icon: suggestion.Icon,
                          color: suggestion.color,
                        };
                      }
                    }
                    if (!assetInfo) return null;
                    const IconComp = assetInfo.Icon;

                    return (
                      <Box
                        key={assetId}
                        sx={{
                          border: `1px solid ${modalAsset.error
                            ? theme.palette.error.main
                            : theme.palette.divider
                            }`,
                          borderRadius: "8px",
                          padding: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IconComp
                              sx={{ fontSize: 18, color: assetInfo.color }}
                            />
                            {modalAsset.type === "otros" ? (
                              <ModalEditableField
                                value={modalAsset.customLabel}
                                onSave={(newValue) => {
                                  handleModalAssetCustomLabelChange(
                                    assetId,
                                    newValue
                                  );
                                }}
                                placeholder="Nombre del canal"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {assetInfo.label}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveModalAsset(assetId);
                            }}
                            sx={{
                              width: "24px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              borderRadius: "4px",
                              "&:hover": {
                                backgroundColor: theme.palette.grey[200],
                              },
                            }}
                          >
                            <X size={16} color={theme.palette.grey[500]} />
                          </Box>
                        </Box>
                        <CustomTextField
                          id={`modal-url-${assetId}`}
                          type="url"
                          value={modalAsset.url || ""}
                          onChange={(e) => {
                            handleModalAssetUrlChange(assetId, e.target.value);
                          }}
                          placeholder={`https://${modalAsset.type !== "otros"
                            ? modalAsset.type
                            : "ejemplo"
                            }.com`}
                          maxLength={300}
                          validateUrl={true}
                          error={modalAsset.error}
                          errorMessage={modalAsset.error}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                <Button variant="outline" onClick={handleCloseAddAssetModal}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmAddAsset}
                  disabled={modalAssets.length === 0 || isSaving}
                >
                  {isSaving ? "Guardando..." : "Agregar"}
                </Button>
              </Box>
            </Box>
          </UnifiedModal>
          {unsavedBar}
        </Card>
      );
    }

    return (
      <Card
        sx={{
          padding: 0,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          maxHeight: "calc(100vh - 200px)",
          minHeight: 0,
          overflow: "hidden",
          flex: 1,
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: 0,
            height: "100%",
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Tabs
            tabs={[
              { value: "basicos", label: "Datos Básicos" },
              { value: "fiscales", label: "Datos Fiscales" },
              { value: "activos", label: "Activos Digitales" },
              { value: "documentacion", label: "Documentación" },
            ]}
            defaultValue={openAssetsTab ? "activos" : "basicos"}
          >
            {/* Tab: Datos Básicos */}
            <TabsContent value="basicos">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  paddingTop: 1,
                  paddingX: "0px",
                  paddingBottom: "16px",
                  height: "100%",
                  overflowY: "auto",
                }}
              >
                {/* Primera línea: Nombre de la empresa, Sector y Pack contratado */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: setupVariantVisible ? 4 : 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Label htmlFor="nombreEmpresa">
                        Nombre de la Empresa
                      </Label>
                      <CustomTextField
                        id="nombreEmpresa"
                        value={formData.nombreEmpresa}
                        onChange={(e) =>
                          handleInputChange(
                            "nombreEmpresa",
                            e.target.value,
                            true
                          )
                        }
                        placeholder="Ej: Restaurante La Buena Mesa"
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: setupVariantVisible ? 4 : 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Label htmlFor="sector">Sector</Label>
                      <CustomSelector
                        value={formData.sector}
                        onChange={(value) => {
                          if (value !== formData.sector) {
                            handleInputChange("sector", value, true);
                          }
                        }}
                        options={sectoresOptions}
                        placeholder="Selecciona un sector o especifica otro"
                        searchable
                        freeSolo
                      />
                    </Box>
                  </Grid>
                  {setupVariantVisible && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {!isClient ? (
                        <InfoTooltip title="Al contratar un pack se crea automáticamente un proyecto de setup, que seguirá la plantilla del pack seleccionado. Una vez iniciado el proyecto de setup, ya no será posible modificar el pack.">
                          <Label htmlFor="packContratado" sx={{ cursor: "help" }}>Pack Contratado</Label>
                        </InfoTooltip>
                      ) : (
                        <Label htmlFor="packContratado">Pack Contratado</Label>
                      )}
                      {(() => {
                        const selector = (
                          <CustomSelector
                            value={formData.packContratado}
                            onChange={(value) => {
                              const normalizedValue =
                                normalizePackSelectorValue(value);
                              if (normalizedValue !== formData.packContratado) {
                                handleInputChange(
                                  "packContratado",
                                  normalizedValue,
                                  true
                                );
                              }
                            }}
                            options={packSelectorOptions}
                            placeholder="Selecciona el pack"
                            disabled={!setupVariantEditable}
                          />
                        );

                        if (!setupVariantEditable && !isClient) {
                          const packLabel = packSelectorOptions.find(p => p.value === formData.packContratado)?.label || formData.packContratado || '';
                          return (
                            <Tooltip
                              title={`Este setup usa la plantilla ${packLabel} y ya tiene fases/tareas completadas, por lo que no puede modificarse. Cambiarlo crearía uno nuevo y se perdería el progreso actual.`}
                              arrow
                              placement="bottom"
                            >
                              <Box sx={{ cursor: "not-allowed", width: "100%" }}>
                                <Box sx={{ pointerEvents: "none", width: "100%" }}>
                                  {selector}
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        }

                        return selector;
                      })()}
                    </Box>
                  </Grid>
                  )}
                </Grid>

                <Separator />

                {/* Segunda línea: Nombre del contacto, Teléfono móvil y Email */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Label htmlFor="nombreContacto">
                        Nombre del Contacto
                      </Label>
                      <CustomTextField
                        id="nombreContacto"
                        value={formData.nombreContacto}
                        onChange={(e) =>
                          handleInputChange(
                            "nombreContacto",
                            e.target.value,
                            true
                          )
                        }
                        placeholder="Ej: Juan Pérez"
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Phone
                          size={16}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Label htmlFor="telefonoContacto">Teléfono Móvil</Label>
                      </Box>
                      <CustomTextField
                        id="telefonoContacto"
                        type="tel"
                        value={formData.telefonoContacto}
                        onChange={(e) =>
                          handleInputChange(
                            "telefonoContacto",
                            e.target.value,
                            true
                          )
                        }
                        placeholder="Ej: +34 123 456 789"
                        maxLength={20}
                        error={validationErrors.telefonoContacto}
                        errorMessage={validationErrors.telefonoContacto}
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Mail
                          size={16}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Label htmlFor="emailContacto">Email</Label>
                      </Box>
                      <CustomTextField
                        id="emailContacto"
                        type="email"
                        value={formData.emailContacto}
                        onChange={(e) =>
                          handleInputChange(
                            "emailContacto",
                            e.target.value,
                            true
                          )
                        }
                        onValidationChange={handleEmailValidation}
                        placeholder="Ej: juan@empresa.com"
                        error={validationErrors.emailContacto}
                        errorMessage={validationErrors.emailContacto}
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Separator />

                {/* Tercera línea: Dirección, Ciudad, Código postal */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <MapPin
                          size={16}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Label htmlFor="direccion">Dirección</Label>
                      </Box>
                      <CustomTextField
                        id="direccion"
                        value={formData.direccion}
                        onChange={(e) =>
                          handleInputChange("direccion", e.target.value, true)
                        }
                        placeholder="Calle, número, piso..."
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Label htmlFor="ciudad">Ciudad</Label>
                      <CustomTextField
                        id="ciudad"
                        value={formData.ciudad}
                        onChange={(e) =>
                          handleInputChange("ciudad", e.target.value, true)
                        }
                        placeholder="Ej: Madrid"
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Label htmlFor="codigoPostal">Código Postal</Label>
                      <Box sx={{ position: "relative" }}>
                        <Hash
                          size={16}
                          style={{
                            position: "absolute",
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: theme.palette.text.secondary,
                            zIndex: 1,
                          }}
                        />
                        <CustomTextField
                          id="codigoPostal"
                          value={formData.codigoPostal}
                          onChange={(e) =>
                            handleInputChange(
                              "codigoPostal",
                              e.target.value,
                              true
                            )
                          }
                          placeholder="28001"
                          sx={{ paddingLeft: "40px" }}
                          setValueOnClickAway={true}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Cuarta línea: Notas */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Label htmlFor="notas">Notas</Label>
                  <CustomTextArea
                    id="notas"
                    rows={3}
                    value={formData.notas}
                    onChange={(e) =>
                      handleInputChange("notas", e.target.value, true)
                    }
                    placeholder="Notas adicionales sobre el cliente..."
                    setValueOnClickAway={true}
                  />
                </Box>

                {!isClientRole && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Label htmlFor="notas_internas">Notas Internas (ocultas al cliente)</Label>
                    <CustomTextArea
                      id="notas_internas"
                      rows={3}
                      value={formData.notas_internas}
                      onChange={(e) =>
                        handleInputChange("notas_internas", e.target.value, true)
                      }
                      placeholder="Notas internas (ocultas al cliente)..."
                      setValueOnClickAway={true}
                    />
                  </Box>
                )}
              </Box>
            </TabsContent>

            {/* Tab: Activos Digitales */}
            <TabsContent value="activos">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  paddingTop: 1,
                  paddingX: "0px",
                  paddingBottom: "16px",
                  height: "100%",
                  overflowY: "auto",
                }}
              >
                {social.dialogs}
                {/* Website - Fila completa */}
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    padding: 2,
                    backgroundColor: "transparent",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Título */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Language
                      sx={{ fontSize: 18, color: theme.palette.text.secondary }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      Página Web
                    </Typography>
                  </Box>

                  <CustomTextField
                    id="website"
                    type="url"
                    value={formData.website}
                    onInput={(e) => {
                      // Limpiar error cuando el usuario escribe
                      if (validationErrors.website) {
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.website;
                          return newErrors;
                        });
                      }
                    }}
                    onChange={(e) => {
                      // Guardar cuando se pierde el foco (gracias a setValueOnClickAway)
                      handleInputChange("website", e.target.value, true);
                    }}
                    placeholder="https://ejemplo.com"
                    maxLength={300}
                    validateUrl={true}
                    error={validationErrors.website}
                    errorMessage={validationErrors.website}
                    setValueOnClickAway={true}
                    isUrlField={true}
                  />
                </Box>

                {showConnections && (
                  <ClientConnections
                    clientId={socialClientId}
                    social={social}
                    renderUrlField={renderSocialUrlField}
                    onSuccess={onSuccess}
                    onError={onError}
                  />
                )}

                {/* Canales de Marketing - Grid de 2 columnas */}
                <Grid container spacing={2}>
                  {linkAssets.map((asset) => {
                    const info = getAssetInfo(asset);
                    if (!info) return null;
                    const IconComp = info.Icon;
                    const fieldError =
                      validationErrors[
                      asset.channel
                        ? `${asset.channel}_url`
                        : `custom_${asset.custom_label}_url`
                      ];
                    const hasContent = asset.url && asset.url.trim() !== "";

                    return (
                      <Grid
                        key={asset.id || asset.channel || asset.custom_label}
                        size={{ xs: 12, sm: 6 }}
                      >
                        <Box
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "8px",
                            padding: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            height: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <IconComp
                                sx={{ fontSize: 18, color: info.color }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: hasContent ? 500 : 400 }}
                              >
                                {info.label}
                              </Typography>
                            </Box>
                            <Tooltip title="Eliminar" arrow>
                              <Box
                                onClick={() => handleRemoveAsset(asset)}
                                sx={{
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#d32f2f",
                                  padding: "6px",
                                  borderRadius: "20px",
                                  transition: "background-color 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                                  },
                                }}
                              >
                                <Trash2 size={16} />
                              </Box>
                            </Tooltip>
                          </Box>

                          <CustomTextField
                            id={`url-${asset.channel || asset.custom_label}`}
                            type="url"
                            value={asset.url || ""}
                            onInput={(e) => {
                              // Limpiar error cuando el usuario escribe
                              const errorKey = asset.channel
                                ? `${asset.channel}_url`
                                : `custom_${asset.custom_label}_url`;
                              if (validationErrors[errorKey]) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors[errorKey];
                                  return newErrors;
                                });
                              }
                            }}
                            onChange={(e) => {
                              // Guardar cuando se pierde el foco (gracias a setValueOnClickAway)
                              const fieldName = asset.channel
                                ? `digital_asset_${asset.channel}`
                                : `custom_asset_${asset.custom_label}`;
                              handleInputChange(fieldName, e.target.value, true);
                            }}
                            placeholder={`https://${asset.channel || "ejemplo"}.com`}
                            maxLength={300}
                            validateUrl={true}
                            error={fieldError}
                            errorMessage={fieldError}
                            setValueOnClickAway={true}
                          />

                        </Box>
                      </Grid>
                    );
                  })}

                  {/* Botón para agregar activo digital */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        border: `2px dashed ${theme.palette.divider}`,
                        borderRadius: "8px",
                        padding: 1.5,
                        cursor: "pointer",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        "&:hover": {
                          borderColor: colors.primary.main,
                          backgroundColor: `${colors.primary.main}05`,
                        },
                      }}
                      onClick={handleOpenAddAssetModal}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Plus
                          size={18}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 400,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Agregar activo digital
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </TabsContent>

            {/* Tab: Datos Fiscales */}
            <TabsContent value="fiscales">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  paddingTop: 1,
                  paddingX: "0px",
                  paddingBottom: "16px",
                  height: "100%",
                  overflowY: "auto",
                }}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Label htmlFor="razonSocial">Razón Social</Label>
                      <CustomTextField
                        id="razonSocial"
                        value={formData.razonSocial}
                        onChange={(e) =>
                          handleInputChange("razonSocial", e.target.value, true)
                        }
                        placeholder="Razón Social"
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Label htmlFor="cifNif">CIF/NIF</Label>
                      <CustomTextField
                        id="cifNif"
                        value={formData.cifNif}
                        onChange={(e) =>
                          handleInputChange("cifNif", e.target.value, true)
                        }
                        placeholder="CIF/NIF"
                        setValueOnClickAway={true}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MapPin
                      size={16}
                      style={{ color: theme.palette.text.secondary }}
                    />
                    <Label htmlFor="direccionFiscal">Dirección Fiscal</Label>
                  </Box>
                  <CustomTextField
                    id="direccionFiscal"
                    value={formData.direccionFiscal}
                    onChange={(e) =>
                      handleInputChange("direccionFiscal", e.target.value, true)
                    }
                    placeholder="Dirección Fiscal Completa"
                    setValueOnClickAway={true}
                  />
                </Box>
              </Box>
            </TabsContent>

            {/* Tab: Documentación */}
            <TabsContent value="documentacion">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  paddingTop: 1,
                  paddingX: "0px",
                  paddingBottom: "16px",
                  height: "100%",
                  overflowY: "auto",
                }}
              >
                {/* Primera fila: Google Drive y NotebookLM */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <GoogleDriveIcon
                          size={19}
                          style={{
                            color: theme.palette.text.secondary,
                          }}
                        />
                        <Label htmlFor="googleDriveLink">
                          Enlace a Google Drive
                        </Label>
                      </Box>
                      <CustomTextField
                        id="googleDriveLink"
                        type="url"
                        value={formData.googleDriveLink}
                        disabled={isClient}
                        onInput={(e) => {
                          // Limpiar error cuando el usuario escribe
                          if (validationErrors.googleDriveLink) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.googleDriveLink;
                              return newErrors;
                            });
                          }
                        }}
                        onChange={(e) => {
                          // Guardar cuando se pierde el foco (gracias a setValueOnClickAway)
                          handleInputChange(
                            "googleDriveLink",
                            e.target.value,
                            true
                          );
                        }}
                        placeholder="Enlace a Google Drive"
                        maxLength={300}
                        validateUrl={true}
                        error={validationErrors.googleDriveLink}
                        errorMessage={validationErrors.googleDriveLink}
                        setValueOnClickAway={true}
                        isUrlField={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <NotebookPen
                          size={16}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Label htmlFor="notebooklmLink">
                          Enlace a NotebookLM
                        </Label>
                      </Box>
                      <CustomTextField
                        id="notebooklmLink"
                        type="url"
                        value={formData.notebooklmLink}
                        disabled={isClient}
                        onInput={(e) => {
                          // Limpiar error cuando el usuario escribe
                          if (validationErrors.notebooklmLink) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.notebooklmLink;
                              return newErrors;
                            });
                          }
                        }}
                        onChange={(e) => {
                          // Guardar cuando se pierde el foco (gracias a setValueOnClickAway)
                          handleInputChange(
                            "notebooklmLink",
                            e.target.value,
                            true
                          );
                        }}
                        placeholder="Enlace a NotebookLM"
                        maxLength={300}
                        validateUrl={true}
                        error={validationErrors.notebooklmLink}
                        errorMessage={validationErrors.notebooklmLink}
                        setValueOnClickAway={true}
                        isUrlField={true}
                      />
                    </Box>
                  </Grid>
                </Grid>

                {/* Segunda fila: Clientify (si no es cliente), Accesos y Panel de Seguimiento */}
                <Grid container spacing={2}>
                  {!isClient && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box
                        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <BarChart
                            sx={{
                              fontSize: 16,
                              color: theme.palette.text.secondary,
                            }}
                          />
                          <Label htmlFor="clientifyLink">
                            Enlace a Clientify
                          </Label>
                        </Box>
                        <CustomTextField
                          id="clientifyLink"
                          type="url"
                          value={formData.clientifyLink}
                          onInput={(e) => {
                            // Limpiar error cuando el usuario escribe
                            if (validationErrors.clientifyLink) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.clientifyLink;
                                return newErrors;
                              });
                            }
                          }}
                          onChange={(e) => {
                            // Guardar cuando se pierde el foco (gracias a setValueOnClickAway)
                            handleInputChange(
                              "clientifyLink",
                              e.target.value,
                              true
                            );
                          }}
                          placeholder="Enlace a Clientify"
                          maxLength={300}
                          validateUrl={true}
                          error={validationErrors.clientifyLink}
                          errorMessage={validationErrors.clientifyLink}
                          setValueOnClickAway={true}
                          isUrlField={true}
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ChecklistRtl
                          sx={{
                            fontSize: 16,
                            color: theme.palette.text.secondary,
                          }}
                        />
                        <Label htmlFor="todolistLink">
                          Accesos y credenciales
                        </Label>
                      </Box>
                      <CustomTextField
                        id="todolistLink"
                        type="url"
                        value={formData.todolistLink}
                        disabled={isClient}
                        onInput={(e) => {
                          // Limpiar error cuando el usuario escribe
                          if (validationErrors.todolistLink) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.todolistLink;
                              return newErrors;
                            });
                          }
                        }}
                        onChange={(e) => {
                          // Guardar cuando se pierde el foco (gracias a setValueOnClickAway)
                          handleInputChange(
                            "todolistLink",
                            e.target.value,
                            true
                          );
                        }}
                        placeholder="Enlace a accesos y credenciales"
                        maxLength={300}
                        validateUrl={true}
                        error={validationErrors.todolistLink}
                        errorMessage={validationErrors.todolistLink}
                        setValueOnClickAway={true}
                        isUrlField={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Activity
                          size={16}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Label htmlFor="trackingPanelLink">
                          Panel de Seguimiento
                        </Label>
                      </Box>
                      <CustomTextField
                        id="trackingPanelLink"
                        type="url"
                        value={formData.trackingPanelLink}
                        disabled={isClient}
                        onInput={(e) => {
                          // Limpiar error cuando el usuario escribe
                          if (validationErrors.trackingPanelLink) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.trackingPanelLink;
                              return newErrors;
                            });
                          }
                        }}
                        onChange={(e) => {
                          // Guardar cuando se pierde el foco (gracias a setValueOnClickAway)
                          handleInputChange(
                            "trackingPanelLink",
                            e.target.value,
                            true
                          );
                        }}
                        placeholder="Enlace al panel de seguimiento"
                        maxLength={300}
                        validateUrl={true}
                        error={validationErrors.trackingPanelLink}
                        errorMessage={validationErrors.trackingPanelLink}
                        setValueOnClickAway={true}
                        isUrlField={true}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Activity
                          size={16}
                          style={{ color: theme.palette.text.secondary }}
                        />
                        <Label htmlFor="visibilityPanelLink">
                          Panel de Visibilidad
                        </Label>
                      </Box>
                      <CustomTextField
                        id="visibilityPanelLink"
                        type="url"
                        value={formData.visibilityPanelLink}
                        disabled={isClient}
                        onInput={(e) => {
                          if (validationErrors.visibilityPanelLink) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.visibilityPanelLink;
                              return newErrors;
                            });
                          }
                        }}
                        onChange={(e) => {
                          handleInputChange(
                            "visibilityPanelLink",
                            e.target.value,
                            true
                          );
                        }}
                        placeholder="Enlace al panel de visibilidad"
                        maxLength={300}
                        validateUrl={true}
                        error={validationErrors.visibilityPanelLink}
                        errorMessage={validationErrors.visibilityPanelLink}
                        setValueOnClickAway={true}
                        isUrlField={true}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </TabsContent>
          </Tabs>
        </CardContent>

        {/* Modal para agregar activo digital */}
        <UnifiedModal
          open={showAddAssetModal}
          onClose={handleCloseAddAssetModal}
          title="Agregar Activos Digitales"
          maxWidth="md"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Selector de tipo de activo con checkboxes */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Label>Selecciona los activos que quieres agregar</Label>
              <Grid container spacing={1}>
                {getAssetTypeOptions().map((option) => {
                  const IconComp = option.Icon;
                  const isSelected =
                    selectedAssetTypes.includes(option.value) ||
                    (option.value === "otros" &&
                      selectedAssetTypes.some((t) => t.startsWith("otros_")));

                  return (
                    <Grid key={option.value} size={{ xs: 12, sm: 6 }}>
                      <Box
                        onClick={() => handleToggleAssetType(option.value)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          padding: 1.5,
                          borderRadius: "8px",
                          border: `1px solid ${isSelected
                            ? colors.primary.main
                            : theme.palette.divider
                            }`,
                          backgroundColor: isSelected
                            ? `${colors.primary.main}08`
                            : "transparent",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? `${colors.primary.main}12`
                              : theme.palette.action.hover,
                            borderColor: isSelected
                              ? colors.primary.main
                              : theme.palette.grey[400],
                          },
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() =>
                            handleToggleAssetType(option.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                        <IconComp sx={{ fontSize: 20, color: option.color }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isSelected ? 500 : 400,
                            color: theme.palette.text.primary,
                            flex: 1,
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Tarjetas de activos seleccionados */}
            {modalAssets.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Separator />
                <Label>Completa la información de los activos</Label>
                <Grid container spacing={2}>
                  {modalAssets.map((modalAsset) => {
                    const assetId = modalAsset.id || modalAsset.type;

                    // Obtener info del asset
                    let assetInfo;
                    if (modalAsset.type === "otros") {
                      assetInfo = {
                        label: modalAsset.customLabel || "Otros",
                        Icon: LinkIcon,
                        color: colors.primary.main,
                        isCustom: true,
                      };
                    } else {
                      const suggestion = sugerencias.find(
                        (s) => s.key === modalAsset.type
                      );
                      if (suggestion) {
                        assetInfo = {
                          label: suggestion.label,
                          Icon: suggestion.Icon,
                          color: suggestion.color,
                          isCustom: false,
                        };
                      }
                    }

                    if (!assetInfo) return null;

                    const IconComp = assetInfo.Icon;
                    const hasContent =
                      modalAsset.url && modalAsset.url.trim() !== "";

                    return (
                      <Grid key={assetId} size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            border: `1px solid ${modalAsset.error
                              ? theme.palette.error.main
                              : theme.palette.divider
                              }`,
                            borderRadius: "8px",
                            backgroundColor: hasContent
                              ? `${colors.primary.main}08`
                              : "transparent",
                            transition: "all 0.2s ease",
                            overflow: "hidden",
                          }}
                        >
                          {/* Header */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding:
                                modalAsset.type === "otros" ? "8px 12px" : 1.5,
                              pb:
                                modalAsset.type === "otros" ? "6px" : undefined,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: modalAsset.type === "otros" ? 0.5 : 1,
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <IconComp
                                sx={{
                                  fontSize: 20,
                                  color: assetInfo.color,
                                  flexShrink: 0,
                                }}
                              />
                              {modalAsset.type === "otros" ? (
                                <ModalEditableField
                                  value={modalAsset.customLabel}
                                  onSave={(newValue) => {
                                    handleModalAssetCustomLabelChange(
                                      assetId,
                                      newValue
                                    );
                                  }}
                                  placeholder="Nombre del canal"
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                  }}
                                >
                                  {assetInfo.label}
                                </Typography>
                              )}
                            </Box>
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveModalAsset(assetId);
                              }}
                              sx={{
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                borderRadius: "4px",
                                "&:hover": {
                                  backgroundColor: theme.palette.grey[200],
                                },
                              }}
                            >
                              <X size={16} color={theme.palette.grey[500]} />
                            </Box>
                          </Box>

                          {/* Content - Campo de URL siempre visible */}
                          <Box sx={{ padding: 2, paddingTop: 0 }}>
                            <CustomTextField
                              id={`modal-url-${assetId}`}
                              type="url"
                              value={modalAsset.url || ""}
                              onChange={(e) => {
                                handleModalAssetUrlChange(
                                  assetId,
                                  e.target.value
                                );
                              }}
                              placeholder={`https://${modalAsset.type !== "otros"
                                ? modalAsset.type
                                : "ejemplo"
                                }.com`}
                              maxLength={300}
                              validateUrl={true}
                              error={modalAsset.error}
                              errorMessage={modalAsset.error}
                              isUrlField={true}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Botones de acción */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 2,
              }}
            >
              <Button variant="outline" onClick={handleCloseAddAssetModal}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmAddAsset}
                disabled={modalAssets.length === 0 || isSaving}
              >
                {isSaving
                  ? "Guardando..."
                  : `Confirmar (${modalAssets.length})`}
              </Button>
            </Box>
          </Box>
        </UnifiedModal>
        {unsavedBar}
      </Card >
    );
  }
);

export const OnboardingFormSection = forwardRef((props, ref) => {
  const {
    clientId,
    selectedFilter,
    clientDetailData,
    clientDetailLoading,
    clientDetailError,
    ...restProps
  } = props;
  const formRefs = useRef({});
  const { colors } = useTheme();

  const shouldUseParentDetail =
    !Array.isArray(clientId) &&
    (clientDetailData !== undefined ||
      clientDetailLoading !== undefined ||
      clientDetailError !== undefined);

  const getClientDetailProps = (id) => {
    if (!shouldUseParentDetail) return {};
    return {
      clientDetailData,
      clientDetailLoading,
      clientDetailError,
    };
  };

  // Asegurarnos de que tenemos un array de clientes para iterar
  // Si clientId es un array (multicliente), lo usamos. Si es un único ID/Objeto, lo metemos en un array.
  let clientsList = Array.isArray(clientId) ? clientId : [clientId];

  // Si hay un filtro seleccionado (y no es global), filtrar la lista para mostrar solo ese
  if (selectedFilter && selectedFilter !== "global") {
    const filteredClient = clientsList.find(c => {
      const id = c?.id || c;
      return String(id) === String(selectedFilter);
    });

    if (filteredClient) {
      clientsList = [filteredClient];
    }
  }

  useImperativeHandle(ref, () => ({
    saveIfHasChanges: async () => {
      const results = [];
      let success = true;
      let message = "Datos guardados exitosamente";

      for (const client of clientsList) {
        const id = client?.id || client;
        const formRef = formRefs.current[id];
        if (formRef) {
          const result = await formRef.saveIfHasChanges();
          results.push(result);
          if (!result.success) {
            success = false;
            message = result.message || "Error al guardar algunos datos";
          }
        }
      }

      // Si todos dicen "No hay cambios", devolvemos eso
      if (results.length > 0 && results.every(r => r.message === "No hay cambios que guardar")) {
        return { success: true, message: "No hay cambios que guardar" };
      }

      return { success, message };
    },
    hasUnsavedChanges: () => {
      return Object.values(formRefs.current).some(ref => ref?.hasUnsavedChanges());
    }
  }));

  if (clientsList.length > 1) {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, width: "100%" }}>
        {clientsList.map((clientData, index) => {
          const id = clientData?.id || clientData;
          const name = clientData?.name || `Empresa ${index + 1}`;

          return (
            <Box
              key={id}
              sx={{
                width: "100%",
                minWidth: 0,
                // Solo poner 2 columnas si la pantalla es realmente grande (>1700px)
                "@media (min-width: 1600px)": {
                  width: "calc(50% - 16px)" // 50% menos la mitad del gap (32px / 2)
                }
              }}
            >
              <Box sx={{
                height: "100%",
                p: 2,
                backgroundColor: "transparent"
              }}>
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600, color: colors.primary.main }}>
                  {name}
                </Typography>
                <SingleOnboardingForm
                  {...restProps}
                  clientId={id}
                  {...getClientDetailProps(id)}
                  ref={el => formRefs.current[id] = el}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  // Caso single client (mantener comportamiento original)
  // Nota: si clientsList tiene 1 elemento que es un objeto, extraemos el ID.
  const singleClient = clientsList[0];
  const singleId = singleClient?.id || singleClient;

  return (
    <SingleOnboardingForm
      {...restProps}
      clientId={singleId}
      {...getClientDetailProps(singleId)}
      ref={ref}
    />
  );
});
